/**
 * Procedural bark for the modelled forest trunks, and the sprite patch that
 * removes the painted trunk those trunks replace.
 *
 * Conifer bark: vertical, irregular plates of grey-brown scale separated by
 * dark furrows, a warmer tone high up, moss creeping up the root flare.
 * Birch bark: chalk-white with horizontal black lenticels and dark patches,
 * turning to black, deeply fissured bark over the lowest metre or so.
 *
 * Both are world-scaled (a plate is the same size on a thin and a thick
 * trunk) and bump the lighting normal from the furrow height, so the bark
 * catches the sun on its ridges.
 */
import type * as THREE from "three";
import { FOREST_SPRITE_TRUNK_U } from "./forestLayout";

export type BarkKind = "conifer" | "birch";

const BARK_COMMON = /* glsl */ `
varying vec3 vBarkLocal;
varying vec3 vBarkWorld;
varying float vBarkGirth;
float barkHash(vec2 p) {
	p = fract(p * vec2(123.34, 456.21));
	p += dot(p, p + 45.32);
	return fract(p.x * p.y);
}
float barkNoise(vec2 p) {
	vec2 i = floor(p);
	vec2 f = fract(p);
	f = f * f * (3.0 - 2.0 * f);
	return mix(mix(barkHash(i), barkHash(i + vec2(1.0, 0.0)), f.x),
		mix(barkHash(i + vec2(0.0, 1.0)), barkHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
// Cell-edge distance of a Voronoi pattern (0 on the furrow).
float barkCells(vec2 p, out float id) {
	vec2 cell = floor(p);
	vec2 local = fract(p);
	float f1 = 8.0;
	float f2 = 8.0;
	id = 0.0;
	for (int j = -1; j <= 1; j++) {
		for (int i = -1; i <= 1; i++) {
			vec2 o = vec2(float(i), float(j));
			vec2 h = vec2(barkHash(cell + o), barkHash(cell + o + 7.1));
			vec2 r = o + h - local;
			float d = dot(r, r);
			if (d < f1) {
				f2 = f1;
				f1 = d;
				id = h.x;
			} else if (d < f2) {
				f2 = d;
			}
		}
	}
	return sqrt(f2) - sqrt(f1);
}
vec3 barkBump(vec3 surfPos, vec3 surfNorm, float height, float scale) {
	vec3 sigmaX = dFdx(surfPos);
	vec3 sigmaY = dFdy(surfPos);
	vec3 r1 = cross(sigmaY, surfNorm);
	vec3 r2 = cross(surfNorm, sigmaX);
	float det = dot(sigmaX, r1);
	vec3 grad = sign(det) * (dFdx(height) * scale * r1 + dFdy(height) * scale * r2);
	return normalize(abs(det) * surfNorm - grad);
}
`;

const CONIFER_COLOUR = /* glsl */ `
// Around the trunk in metres of circumference, up it in metres.
vec2 barkUV = vec2(atan(vBarkLocal.z, vBarkLocal.x) * vBarkGirth, vBarkWorld.y);
float barkId;
float barkEdge = barkCells(barkUV * vec2(7.0, 2.6), barkId);
float barkFine = barkNoise(barkUV * vec2(24.0, 6.0));
float barkHeight = smoothstep(0.01, 0.16, barkEdge) * (0.75 + 0.25 * barkFine);
vec3 barkPlate = mix(vec3(0.11, 0.08, 0.06), vec3(0.2, 0.145, 0.105), barkId);
// Old spruce and pine turn warmer and redder up the stem.
barkPlate = mix(barkPlate, vec3(0.3, 0.15, 0.08), smoothstep(0.3, 0.65, vBarkLocal.y) * 0.7);
vec3 barkColour = mix(vec3(0.035, 0.028, 0.022), barkPlate * (0.8 + 0.4 * barkFine), barkHeight);
float barkMoss = (1.0 - smoothstep(0.0, 0.07, vBarkLocal.y))
	* smoothstep(0.35, 0.65, barkNoise(barkUV * 2.3) + barkHeight * 0.3);
barkColour = mix(barkColour, vec3(0.16, 0.22, 0.06) * (0.8 + 0.4 * barkFine), barkMoss * 0.85);
// The crown shades the stem it closes around.
barkColour *= 1.0 - 0.45 * smoothstep(0.2, 0.55, vBarkLocal.y);
diffuseColor.rgb = barkColour;
`;

const BIRCH_COLOUR = /* glsl */ `
vec2 barkUV = vec2(atan(vBarkLocal.z, vBarkLocal.x) * vBarkGirth, vBarkWorld.y);
float barkFine = barkNoise(barkUV * vec2(18.0, 40.0));
// Lenticels: short horizontal dashes.
float barkDash = smoothstep(0.72, 0.8, barkNoise(barkUV * vec2(6.0, 55.0)));
float barkPatch = smoothstep(0.62, 0.72, barkNoise(barkUV * vec2(2.5, 1.6) + 4.0));
vec3 barkColour = vec3(0.8, 0.79, 0.74) * (0.9 + 0.1 * barkFine);
barkColour = mix(barkColour, vec3(0.08, 0.075, 0.07), max(barkDash, barkPatch * 0.85));
// The foot turns black and fissured.
float barkId;
float barkEdge = barkCells(barkUV * vec2(6.0, 3.0), barkId);
float barkFoot = 1.0 - smoothstep(0.06, 0.16 + 0.05 * barkNoise(barkUV * 3.0), vBarkLocal.y);
vec3 barkRough = mix(vec3(0.05, 0.045, 0.04), vec3(0.3, 0.29, 0.26), smoothstep(0.05, 0.25, barkEdge));
barkColour = mix(barkColour, barkRough, barkFoot);
barkColour *= 1.0 - 0.35 * smoothstep(0.45, 0.75, vBarkLocal.y);
float barkHeight = mix(0.8 + 0.2 * barkFine - barkDash * 0.3, smoothstep(0.02, 0.25, barkEdge), barkFoot);
diffuseColor.rgb = barkColour;
`;

/**
 * The scene fog is tuned for the lawn's far edge; at forest distance it
 * washed the nearest trunks and crowns to ghosts. Forest materials take a
 * fraction of it, so the treeline keeps its contrast and colour while the
 * back rows still fade into the haze.
 */
export const FOREST_FOG_SHARE = 0.62;

const SOFT_FOG_FRAGMENT = /* glsl */ `
#ifdef USE_FOG
	#ifdef FOG_EXP2
		float fogFactor = 1.0 - exp(- fogDensity * fogDensity * vFogDepth * vFogDepth);
	#else
		float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
	#endif
	gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, fogFactor * ${FOREST_FOG_SHARE.toFixed(2)});
#endif`;

function softenFog(shader: THREE.WebGLProgramParametersWithUniforms) {
	shader.fragmentShader = shader.fragmentShader.replace(
		"#include <fog_fragment>",
		SOFT_FOG_FRAGMENT,
	);
}

export function applyBark(
	material: THREE.MeshStandardMaterial,
	kind: BarkKind,
): THREE.MeshStandardMaterial {
	material.onBeforeCompile = (shader) => {
		softenFog(shader);
		shader.vertexShader = shader.vertexShader
			.replace(
				"#include <common>",
				`#include <common>
varying vec3 vBarkLocal;
varying vec3 vBarkWorld;
varying float vBarkGirth;`,
			)
			.replace(
				"#include <begin_vertex>",
				`#include <begin_vertex>
mat4 barkModel = modelMatrix;
#ifdef USE_INSTANCING
barkModel = modelMatrix * instanceMatrix;
#endif
vBarkLocal = position;
vBarkWorld = (barkModel * vec4(position, 1.0)).xyz;
// World radius of this instance at unit local radius.
vBarkGirth = length(barkModel[0].xyz);`,
			);
		shader.fragmentShader = shader.fragmentShader
			.replace("#include <common>", `#include <common>\n${BARK_COMMON}`)
			.replace(
				"#include <color_fragment>",
				`#include <color_fragment>\n${kind === "birch" ? BIRCH_COLOUR : CONIFER_COLOUR}`,
			)
			.replace(
				"#include <normal_fragment_maps>",
				`#include <normal_fragment_maps>
normal = barkBump(-vViewPosition, normal, barkHeight, ${kind === "birch" ? "0.012" : "0.035"});`,
			);
	};
	material.customProgramCacheKey = () => `forest-bark-v2|${kind}`;
	material.needsUpdate = true;
	return material;
}

/**
 * Sprite patch: drop the painted trunk under `trunkTop` (a fraction of the
 * sprite height) wherever the texel is not foliage-green, so the modelled
 * trunk is the only one. Needles and leaves (green-dominant) stay.
 */
export function applySpriteTrunkMask(
	material: THREE.MeshBasicMaterial,
	trunkTop: number,
): THREE.MeshBasicMaterial {
	material.onBeforeCompile = (shader) => {
		softenFog(shader);
		shader.fragmentShader = shader.fragmentShader.replace(
			"#include <alphatest_fragment>",
			`#include <alphatest_fragment>
#ifdef USE_MAP
{
	vec2 spriteUv = vMapUv;
	float nearAxis = 1.0 - step(0.035 + 0.1 * (1.0 - smoothstep(0.0, 0.07, spriteUv.y)), abs(spriteUv.x - ${FOREST_SPRITE_TRUNK_U.toFixed(4)}));
	vec4 texel = texture2D(map, spriteUv);
	float foliage = step(max(texel.r, texel.b) + 0.015, texel.g);
	if (spriteUv.y < ${trunkTop.toFixed(3)} && nearAxis > 0.5 && foliage < 0.5) discard;
}
#endif`,
		);
	};
	material.customProgramCacheKey = () =>
		`forest-sprite-trunk-mask-v2|${trunkTop}`;
	material.needsUpdate = true;
	return material;
}
